import { CmdSimpleSelectOption } from '@commander-services/gui-components';
import { AxiosResponse } from 'axios';
import { IValidationApiResponse } from '../../Services/FormikValidatorService';
import { TABLE_PERSON } from '../../Services/TableService';
import { IAdditionalValues } from './Form';
import {
  CustomerSettingsApiResponse,
  EmployeeApiResponse,
  IEmployeeChipOption,
  IEmployeeData,
  IEmployeeFormError,
  IRoleOption,
  IUserData,
} from './types';
import {
  getRequest,
  IHttpServiceOptions,
  patchRequest,
  postRequest,
  putRequest,
} from '../../Services/HttpService';

export const IS_DRIVER = 'driver';
export const IS_USER = 'user';
export const IS_PENDING = 'pending';
export const IS_ACTIVE = 'active';

const getHttpServiceOptions = (additionalValues?: IAdditionalValues | null) => {
  let httpServiceOptions: IHttpServiceOptions | undefined;
  if (additionalValues) {
    httpServiceOptions = {
      queryParams: additionalValues as Record<string, string>,
    };
  }
  return httpServiceOptions;
};

/**
 * Get customers
 * @returns {Promise<CmdSimpleSelectOption[] | null>}
 */
export const getCustomers = async (): Promise<CmdSimpleSelectOption[] | null> => {
  const response = await getRequest<CmdSimpleSelectOption[] | false>('v1/person/form/customerId');
  if (response) {
    // console.log('getCustomers: ', response.data);
    return response as CmdSimpleSelectOption[];
  }
  return null;
};

export const getDrivers = async (
  additionalValues: IAdditionalValues | null
): Promise<CmdSimpleSelectOption[]> => {
  const httpServiceOptions = getHttpServiceOptions(additionalValues);
  const response = await getRequest<CmdSimpleSelectOption[] | false>(
    'v1/person/form/drivers',
    httpServiceOptions
  );
  if (response) {
    return response as CmdSimpleSelectOption[];
  }
  return [];
};

export const getVehicles = async (
  additionalValues: IAdditionalValues | null
): Promise<CmdSimpleSelectOption[]> => {
  const httpServiceOptions = getHttpServiceOptions(additionalValues);
  const response = await getRequest<CmdSimpleSelectOption[] | false>(
    'v1/person/form/vehicles',
    httpServiceOptions
  );
  if (response) {
    return response as CmdSimpleSelectOption[];
  }
  return [];
};

export const getRoles = async (
  additionalValues: IAdditionalValues | null
): Promise<IRoleOption[] | null> => {
  const httpServiceOptions = getHttpServiceOptions(additionalValues);
  const response = await getRequest<IRoleOption[] | false>(
    'v1/person/form/roles',
    httpServiceOptions
  );
  if (response) {
    // console.log('getCustomers: ', response.data);
    return response as IRoleOption[];
  }
  return [];
};

/**
 * Get identification cards
 * @param {number | null | undefined} editId
 * @returns {Promise<CmdSimpleSelectOption[] | null>}
 */
export const getIdentificationCards = async (
  customerId: number,
  editId?: number | null
): Promise<CmdSimpleSelectOption[] | null> => {
  const response = await getRequest<CmdSimpleSelectOption[] | false>(
    `v1/person/form/identCards?customerId=${customerId}${editId ? `&id=${editId}` : ''}`
  );
  if (response) {
    return response as CmdSimpleSelectOption[];
  }
  return null;
};

/**
 * Get work departments
 * @params {number} customerId
 * @returns {Promise<CmdSimpleSelectOption[] | null>}
 */
export const getWorkDepartments = async (
  customerId: number
): Promise<CmdSimpleSelectOption[] | null> => {
  const response = await getRequest<CmdSimpleSelectOption[] | false>(
    `v1/person/form/workDepartmentId?customerId=${customerId}`
  );
  if (response) {
    return response as CmdSimpleSelectOption[];
  }
  return null;
};

/**
 * Get cost centers
 * @param {number} customerId
 * @returns {Promise<CmdSimpleSelectOption[] | null>}
 */
export const getCostCenters = async (
  customerId: number
): Promise<CmdSimpleSelectOption[] | null> => {
  const response = await getRequest<CmdSimpleSelectOption[] | false>(
    `v1/person/form/costCenterId?customerId=${customerId}`
  );
  if (response) {
    return response as CmdSimpleSelectOption[];
  }
  return null;
};

/**
 * Get validation
 * @returns {Promise<IValidationApiResponse | null>}
 */
export const getValidation = async (
  additionalValues: IAdditionalValues | null
): Promise<IValidationApiResponse | null> => {
  const httpServiceOptions = getHttpServiceOptions(additionalValues);
  const response = await getRequest<IValidationApiResponse | false>(
    'v1/person/validation',
    httpServiceOptions
  );
  if (response) {
    // console.log('getValidation: ', response.data);
    return response as IValidationApiResponse;
  }
  return null;
};

/**
 * Get employee
 * @param {number} id
 * @returns {Promise<IEmployeeData | null>}
 */
export const get = async (id: number): Promise<IEmployeeData | null> => {
  const response = await getRequest<EmployeeApiResponse | false>(`v1/person/${id}`);
  if (response) {
    return (response as EmployeeApiResponse).person;
  }
  return null;
};

/**
 * Get user
 * @param {number} id
 * @returns {Promise<IUserData | null>}
 */
export const user = async (id: number): Promise<IUserData | null> => {
  const response = await getRequest<IUserData | false>(`v1/person/${id}/user`, {
    showErrorToaster: false,
  });
  if (response) {
    return response as IUserData;
  }
  return null;
};

/**
 * Save employee
 * @param {IEmployeeData} data
 * @returns {Promise<IEmployeeData | IEmployeeFormError | null>}
 */
export const save = async (
  data: IEmployeeData
): Promise<IEmployeeData | IEmployeeFormError | null> => {
  const response = await postRequest('v1/person/add', data, {
    requestTimeout: 60000,
    showToaster: true,
    returnAxioErrorResponseData: true,
    showErrorToaster: false,
  });
  if (response) {
    return response as IEmployeeData | IEmployeeFormError;
  }
  return null;
};

/**
 * Update employee
 * @param {number} id
 * @param {IEmployeeData} data
 * @returns {Promise<IEmployeeData | null>}
 */
export const update = async (
  id: number,
  data: IEmployeeData
): Promise<IEmployeeData | IEmployeeFormError | AxiosResponse | null> => {
  const response = await putRequest<IEmployeeData, IEmployeeFormError | AxiosResponse | false>(
    `v1/person/${id}/edit`,
    data,
    {
      showToaster: true,
      showErrorToaster: false,
      returnAxiosResponse: true,
      returnAxioErrorResponseData: true,
    }
  );

  if (response) {
    return response;
  }
  return null;
};

/**
 * User exist?
 * @param {string} firstName
 * @param {string} lastName
 * @param {number} customerId
 * @returns {Promise<boolean>}
 */
export const userExists = async (
  firstName: string,
  lastName: string,
  customerId: number
): Promise<boolean> => {
  const response = await getRequest(
    `v1/person/check?firstName=${firstName}&lastName=${lastName}&customerId=${customerId}`,
    { showToaster: false, showErrorToaster: false }
  );
  if (response) {
    // console.log('userExists: ', response.data);
    return false;
  }
  return true;
};

export const remove = async (id: string, customerId: number): Promise<boolean> => {
  const response = await putRequest(
    `/v1/${TABLE_PERSON}/${id}/remove?customerId=${customerId}`,
    { id },
    { showToaster: true }
  );
  if (response) {
    return true;
  }
  return false;
};

export const reactivate = async (id: string): Promise<boolean> => {
  const response = await patchRequest(`/v1/user/${id}/reactivate`, {});
  return !!response;
};

export const activate = async (id: string): Promise<boolean> => {
  const response = await patchRequest(`/v1/user/${id}/initiate-activation`, {});
  return !!response;
};

export const deactivate = async (id: string, reason: string): Promise<boolean> => {
  const response = await patchRequest(`/v1/user/${id}/deactivate`, { reason });
  return !!response;
};

export const reset = async (id: string): Promise<boolean> => {
  const response = await patchRequest(`/v1/user/${id}/reset-password`, {});
  return !!response;
};

export const reactivateMultiple = async (ids: string[]): Promise<boolean> => {
  const response = await patchRequest(`/v1/user/reactivate`, { ids });
  return !!response;
};

export const activateMultiple = async (ids: string[]): Promise<boolean> => {
  const response = await patchRequest(`/v1/user/initiate-activation`, { ids });
  return !!response;
};

export const deactivateMultiple = async (ids: string[], reason: string): Promise<boolean> => {
  const response = await patchRequest(`/v1/user/deactivate`, { ids, reason });
  return !!response;
};

export const resetMultiple = async (ids: string[]): Promise<boolean> => {
  const response = await patchRequest(`/v1/user/reset-password`, { ids });
  return !!response;
};

export const getCustomerSettings = async (customerId: string): Promise<boolean | null> => {
  const response = await getRequest<CustomerSettingsApiResponse | false>(
    `v1/customer/${customerId}/settings`
  );
  if (response) {
    // console.log('getValidation: ', response.data);
    return (response as CustomerSettingsApiResponse).general?.twoFactorAuth;
  }
  return null;
};

export const handleChipOptionsCategory = (
  options: IEmployeeChipOption[],
  selectedValues: string[] | { [key: string]: string[] },
  categoriesName: string[]
) => {
  if (Object.values(selectedValues).length === 0) {
    return {
      [categoriesName[0]]: [],
      [categoriesName[1]]: options.filter((option) => !option.deleted),
    };
  }

  const filteredSelectedValues: string[] = Array.isArray(selectedValues)
    ? selectedValues
    : Object.values(selectedValues).reduce((acc, val) => acc.concat(val), []);

  const result = options.reduce(
    (acc, item) => {
      const isSelected = filteredSelectedValues.includes(String(item.value));
      if (isSelected) {
        acc[categoriesName[0]].push(item);
      } else if (!item.deleted) {
        acc[categoriesName[1]].push(item);
      }
      return acc;
    },
    {
      [categoriesName[0]]: [] as IEmployeeChipOption[],
      [categoriesName[1]]: [] as IEmployeeChipOption[],
    }
  );

  return result;
};

export const getHours = (date: Date) => {
  const hours = String(date.getHours());
  return hours.length <= 1 ? `0${hours}` : hours;
};

export const getMinutes = (date: Date) => {
  const minutes = String(date.getMinutes());
  return minutes.length <= 1 ? `0${minutes}` : minutes;
};

export const getTime = (timeString: string | undefined) => {
  if (!timeString) {
    return new Date();
  }
  const timeArr = timeString.split(':');
  if (timeArr.length === 2) {
    return new Date(new Date().setHours(Number(timeArr[0]), Number(timeArr[1]), 0, 0));
  }
  return new Date();
};

export const isVehicleAllowed = (
  roles: string[] | undefined,
  rolesOptions: IRoleOption[]
): boolean => {
  if (!roles || roles.length === 0) {
    return false; // Ak nie sú definované alebo prázdne, vrátia true
  }

  const filteredRolesOptions = rolesOptions.filter((i) => roles.includes(i.value));

  const hasAdminVehicleScope = filteredRolesOptions.some(
    (i) => i.vehicleScope === 'admin' || i.vehicleGroupScope === 'admin'
  );

  if (hasAdminVehicleScope) {
    return false;
  }

  const vehicleScope = filteredRolesOptions.find(
    (i) => i.vehicleScope === 'user_related' || i.vehicleGroupScope === 'user_related'
  );

  return !!vehicleScope;
};

export const isDriverAllowed = (
  roles: string[] | undefined,
  rolesOptions: IRoleOption[]
): boolean => {
  if (!roles || roles.length === 0) {
    return false;
  }

  const filteredRolesOptions = rolesOptions.filter((i) => roles.includes(i.value));

  const hasAdminDriverScope = filteredRolesOptions.some((i) => i.driverScope === 'admin');

  if (hasAdminDriverScope) {
    return false;
  }

  const driverScope = filteredRolesOptions.find((i) => i.driverScope === 'user_related');

  return !!driverScope;
};
